import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../../pages/aai/useAsync';
import type { ActivityEvent, ActivityFeed, MyPreferencesResponse, RosterMember } from '../../types/aai';
import {
  Action, Banner, Chip, Divided, EmptyState, ErrorNote, Kicker, ListRow, Loading, Panel,
  Select, SettingRow, Toggle,
} from '../aa';
import { EventDrawer } from '../../pages/partner/ActivityView';
import { relativeTime } from '../../pages/partner/format';
import { roleLabel } from './OwnerSettingsPanels';
import { ConnectorsPanel } from './connectors/ConnectorsPanel';

/*
 * The Department Head's settings panels. The same sections the Owner has,
 * rendered for a role that reads and requests rather than configures. Every
 * panel here is fed by a read the role already holds; anything Owner-managed
 * is shown as such with the control disabled and the reason stated.
 */

const closeSettings = () => window.dispatchEvent(new Event('settings:close'));

const onboardingChip = (onboarded: boolean): React.ReactNode => {
  if (onboarded) return <Chip tone="ok">Onboarded</Chip>;
  return <Chip>Invited</Chip>;
};

const initialsOf = (name: string): string => name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || 'A';

/* ── Connectors ────────────────────────────────────────────────────────── */

export const DepartmentAppsPanel: React.FC = () => (
  <div className="mx-auto max-w-[1000px]">
    <ConnectorsPanel mode="department" />
    <p className="m-0 mt-4 text-[12.5px] leading-[20px] text-ink-dim">Personal app connections are each person's own and are never listed to the department.</p>
  </div>
);

/* ── AI performance ────────────────────────────────────────────────────── */

const PolicyTile: React.FC<{ kicker: string; title: string; desc: string }> = ({ kicker, title, desc }) => (
  <div className="rounded-panel border border-line bg-white px-5 py-4">
    <Kicker>{kicker}</Kicker>
    <div className="mt-2 text-[15px] font-bold leading-[21px] text-ink">{title}</div>
    <div className="mt-1 text-[12.5px] leading-[19px] text-ink-dim">{desc}</div>
  </div>
);

/** The read-only AI policy panel. The Department Head and the employee read the
 *  same boundary; only the place "View AI capabilities" lands differs. */
export const PolicyPanel: React.FC<{ capabilitiesRoute: string }> = ({ capabilitiesRoute }) => {
  const navigate = useNavigate();
  const prefs = useAsync<MyPreferencesResponse>(() => aaiApi.getMyPreferences(), 'Could not load the AI policy.');
  if (prefs.loading) return <Loading label="Loading AI policy" />;
  if (prefs.error || !prefs.data) return <ErrorNote message={prefs.error || 'Something went wrong.'} />;
  const b = prefs.data.boundary;

  return (
    <div className="mx-auto max-w-[1000px]">
      <Banner tone="quiet" size="lg" kicker="Current policy" title={b.ai.policy} action={<Chip tone="private">Protected</Chip>} />
      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <PolicyTile kicker="Protected" title="Workflows & sources" desc="Tools · permissions · owners" />
        <PolicyTile kicker="Routing" title={b.ai.routing || 'Approved pool'} desc="Policy controlled" />
        <PolicyTile kicker="Release" title={b.ai.release || 'Evidence required'} desc="Evaluation · rollout · fallback" />
      </div>
      <Panel className="mt-5">
        <div className="px-6 py-5">
          <label className="mb-2 block text-[13px] font-semibold text-ink" htmlFor="dept-ai-policy">Default policy</label>
          <Select id="dept-ai-policy" value={b.ai.policy} disabled onChange={() => undefined}><option value={b.ai.policy}>{b.ai.policy}</option></Select>
          <p className="m-0 mt-2 text-[12.5px] text-ink-dim">High-risk work may use a fixed approved route. Set by {b.managed_by || 'the program'}.</p>
        </div>
      </Panel>
      <div className="mt-5 rounded-panel border border-line bg-white px-6">
        <SettingRow title="Require representative evaluation" desc="Work-specific quality and policy checks" control={<Toggle checked onChange={() => undefined} label="Require representative evaluation" disabled />} />
        <SettingRow title="Keep approved fallback" desc="Previous approved route" control={<Toggle checked onChange={() => undefined} label="Keep approved fallback" disabled />} />
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Action size="lg" onClick={() => { closeSettings(); navigate(capabilitiesRoute); }}>View AI capabilities</Action>
        <Action size="lg" variant="quiet" disabled reason="AI policy is set by the Owner and the AI Champion">Change policy</Action>
      </div>
    </div>
  );
};

export const DepartmentAiPanel: React.FC = () => <PolicyPanel capabilitiesRoute="/projects?tab=quick_wins" />;

export const EmployeeAiPanel: React.FC = () => <PolicyPanel capabilitiesRoute="/my-projects?tab=quick_wins" />;

/* ── People & departments ──────────────────────────────────────────────── */

export const DepartmentPeoplePanel: React.FC = () => {
  const members = useAsync<{ members: RosterMember[]; department: string | null }>(() => aaiApi.getMembers(), 'Could not load people.');
  const people = members.data?.members || [];
  const department = members.data?.department || 'your department';
  let peopleWord = 'people';
  if (people.length === 1) peopleWord = 'person';

  return (
    <div className="mx-auto max-w-[1000px]">
      <h3 className="m-0 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink">{people.length} {peopleWord}</h3>
      <p className="m-0 mb-4 mt-1.5 text-[14px] leading-[23px] text-ink-muted">Everyone placed in {department}. Names and onboarding status only; readiness stays an aggregate. Placement and roles are managed by the Owner.</p>
      <Panel>
        {members.loading && <Loading label="Loading people" />}
        {members.error && <div className="px-6 py-5"><ErrorNote message={members.error} /></div>}
        {!members.loading && !members.error && people.length === 0 && <EmptyState title="Nobody placed yet" reason="People appear here once the Owner places them in your department." />}
        <Divided>
          {people.map((m) => (
            <ListRow
              key={m.email}
              mark={<span className="grid h-[37px] w-[37px] shrink-0 place-items-center rounded-tile bg-canvas-inset text-[12px] font-bold text-ink">{initialsOf(m.name || m.email)}</span>}
              title={m.name || m.email}
              desc={`${roleLabel(m.workspace_role)} · ${m.department || 'No department'}`}
              right={onboardingChip(m.onboarded)}
            />
          ))}
        </Divided>
      </Panel>
      <p className="m-0 mt-4 text-[12.5px] leading-[20px] text-ink-dim">Owner managed. To invite someone or move them between departments, ask the Owner.</p>
    </div>
  );
};

/* ── Activity & audit ──────────────────────────────────────────────────── */

export const DepartmentActivityPanel: React.FC<{ clientName: string }> = ({ clientName }) => {
  const feed = useAsync<ActivityFeed>(() => aaiApi.getActivity('department', 100), 'Could not load activity.');
  const [selected, setSelected] = useState<ActivityEvent | null>(null);
  const events = feed.data?.events || [];
  const count = events.length;
  let eventsTitle = `${count} visible events`;
  if (count === 1) eventsTitle = '1 visible event';

  return (
    <div className="mx-auto max-w-[1000px]">
      <h3 className="m-0 text-[26px] font-bold leading-[32px] tracking-[-0.015em] text-ink">{eventsTitle}</h3>
      <p className="m-0 mb-5 mt-2 text-[14px] leading-[23px] text-ink-muted">
        Visibility follows the linked person, department, Project, client assignment, or control entitlement. The activity label does not grant access.
      </p>
      {feed.loading && <Loading label="Loading activity" />}
      {feed.error && <ErrorNote message={feed.error} />}
      {!feed.loading && !feed.error && count === 0 && <EmptyState title="Nothing recorded yet" reason="Decisions, support requests and Project changes in your department appear here as they happen." />}
      {count > 0 && (
        <Panel>
          <Divided>
            {events.map((e) => (
              <ListRow
                key={e.id}
                mark={<span className="grid h-[37px] w-[37px] shrink-0 place-items-center rounded-tile bg-canvas-inset text-[12px] font-bold text-ink">A</span>}
                title={e.label}
                foot={e.entity_title || undefined}
                right={<span className="text-[12.5px] text-ink-dim">{relativeTime(e.created_at)}</span>}
                onClick={() => setSelected(e)}
                arrow
              />
            ))}
          </Divided>
        </Panel>
      )}
      <div className="mt-5 border-l-[3px] border-ink bg-canvas-inset px-5 py-4 text-[14px] text-ink">
        Export requires a separate, time-limited Audit exporter assignment.
      </div>
      {selected && <EventDrawer event={selected} clientName={clientName} kicker="Activity" layer="modal" onClose={() => setSelected(null)} />}
    </div>
  );
};

/* ── Section list ──────────────────────────────────────────────────────── */

export const departmentSectionNote = 'Same sections as the Owner, different permissions. Owner-managed controls are shown disabled with the reason.';
